import {
	ArrowDownOutlined,
	ArrowUpOutlined,
	CheckCircleTwoTone,
	EditOutlined,
	FileTextOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { Button, Input, Space, Table, Tooltip } from 'antd';
import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../../../../lib/shell/PageLayout/PageLayout';
import { CapabilityBadges } from '../components/unified/CapabilityBadges';
import { CapabilityFilterBar } from '../components/unified/CapabilityFilterBar';
import { ClientSearchResult, useClientsSearch } from '../hooks/useClientsSearch';

const PAGE_SIZE = 10;

const SORTS = [
	{ value: 'id', label: 'ID' },
	{ value: 'name', label: 'Nome' },
	{ value: 'initials', label: 'Sigla' },
];

const clamped = (value: string, max: number) => {
	const text = value || '—';

	if (text.length <= max) {
		return <span>{text}</span>;
	}

	return (
		<Tooltip title={text} placement='topLeft'>
			<span style={{ cursor: 'help' }}>{`${text.slice(0, max).trimEnd()}…`}</span>
		</Tooltip>
	);
};

export const UnifiedClientsListPage: FC = () => {
	const [text, set_text] = useState('');
	const [selected, set_selected] = useState<string[]>([]);
	const [page, set_page] = useState(1);
	const [limit, set_limit] = useState(PAGE_SIZE);
	const [sort, set_sort] = useState('id');
	const [sort_dir, set_sort_dir] = useState<'asc' | 'desc'>('asc');

	const { data, loading } = useClientsSearch({ text, selected, page, limit, sort, sort_dir });

	const toggle = (token: string) => {
		set_page(1);
		set_selected((prev) =>
			prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token]
		);
	};

	const change_sort = (value: string) => {
		if (value === sort) {
			set_sort_dir(sort_dir === 'asc' ? 'desc' : 'asc');
		} else {
			set_sort(value);
			set_sort_dir('asc');
		}
		set_page(1);
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			width: 100,
			render: (id: string) => (
				<Link to={`/plataforma/clientes/${id}`} style={{ color: '#003a70', fontWeight: 600 }}>
					#{id}
				</Link>
			),
		},
		{
			title: 'SIGLA',
			dataIndex: 'initials',
			width: 170,
			render: (initials: string) => clamped(initials, 18),
		},
		{
			title: 'NOME',
			dataIndex: 'name',
			width: 260,
			render: (name: string) => clamped(name, 34),
		},
		{
			title: 'ITENS DE CONTRATO',
			width: 280,
			render: (_: any, record: ClientSearchResult) => <CapabilityBadges client={record} />,
		},
		{
			title: 'ILIMITADO',
			dataIndex: 'unlimited',
			width: 110,
			align: 'center' as const,
			render: (unlimited: boolean) =>
				unlimited ? (
					<Tooltip title='Acesso ilimitado'>
						<CheckCircleTwoTone twoToneColor='#52c41a' style={{ fontSize: 16 }} />
					</Tooltip>
				) : (
					<span style={{ color: '#bfbfbf' }}>—</span>
				),
		},
		{
			title: 'AÇÕES',
			width: 210,
			align: 'center' as const,
			render: (_: any, record: ClientSearchResult) => (
				<Space size={8}>
					<Link to={`/plataforma/clientes/${record.id}`}>
						<Button type='primary' icon={<EditOutlined />}>
							Editar
						</Button>
					</Link>
					<Link to={`/plataforma/clientes/${record.id}?tab=contracts`}>
						<Button icon={<FileTextOutlined />}>Contratos</Button>
					</Link>
				</Space>
			),
		},
	];

	return (
		<PageLayout title='Clientes'>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 16,
				}}
			>
				<span style={{ color: '#8c8c8c' }}>{data ? `${data.total} clientes` : ' '}</span>
				<Link to='/plataforma/clientes/novo'>
					<Button type='primary' icon={<PlusOutlined />}>
						Adicionar cliente
					</Button>
				</Link>
			</div>

			<div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
				<Input.Search
					allowClear
					placeholder='Buscar por nome, sigla ou ID...'
					onSearch={(v) => {
						set_page(1);
						set_text(v);
					}}
					style={{ flex: 1 }}
				/>
				<Space.Compact>
					{SORTS.map((s) => {
						const active = sort === s.value;

						return (
							<Tooltip
								key={s.value}
								title={
									active
										? `Ordenando por ${s.label} (${
												sort_dir === 'asc' ? 'crescente' : 'decrescente'
										  }) — clique para inverter`
										: `Ordenar por ${s.label}`
								}
							>
								<Button
									type={active ? 'primary' : 'default'}
									onClick={() => change_sort(s.value)}
									icon={
										active ? (
											sort_dir === 'asc' ? (
												<ArrowUpOutlined />
											) : (
												<ArrowDownOutlined />
											)
										) : undefined
									}
								>
									{s.label}
								</Button>
							</Tooltip>
						);
					})}
				</Space.Compact>
			</div>

			<div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
				<CapabilityFilterBar
					selected={selected}
					facets={data?.facets}
					on_toggle={toggle}
					on_clear={() => {
						set_selected([]);
						set_page(1);
					}}
				/>
			</div>

			<Table
				rowKey='id'
				size='middle'
				tableLayout='fixed'
				loading={loading}
				columns={columns}
				dataSource={data?.clients || []}
				scroll={{ x: 1130 }}
				pagination={{
					current: page,
					pageSize: limit,
					total: data?.total || 0,
					showSizeChanger: true,
					pageSizeOptions: ['10', '20', '50'],
					onChange: (p, ps) => {
						set_page(p);
						set_limit(ps || PAGE_SIZE);
					},
				}}
			/>
		</PageLayout>
	);
};
